(function () {
  function notifyReady() {
    document.dispatchEvent(new CustomEvent('item-manager:theme-utils-ready'));
  }

  if (window.ItemManagerThemeUtils) {
    notifyReady();
    return;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[char];
    });
  }

  function getStorage(name) {
    try {
      return window[name] || null;
    } catch (error) {
      return null;
    }
  }

  function readStorageItem(storage, key) {
    try {
      return storage ? storage.getItem(key) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStorageItem(storage, key, value) {
    try {
      if (storage) storage.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in privacy modes; rendering should continue.
    }
  }

  function removeStorageItem(storage, key) {
    try {
      if (storage) storage.removeItem(key);
    } catch (error) {
      // Best-effort cleanup only.
    }
  }

  function readJsonCache(options) {
    var now = Date.now();
    var stores = options.stores || [getStorage('sessionStorage'), getStorage('localStorage')];
    var staleTtl = Number(options.staleTtl || options.cacheTtl || 0);
    var freshTtl = Number(options.cacheTtl || staleTtl);

    for (var index = 0; index < stores.length; index += 1) {
      var storage = stores[index];
      var raw = readStorageItem(storage, options.key);
      if (!raw) continue;

      try {
        var cached = JSON.parse(raw);
        var age = now - Number(cached && cached.createdAt || 0);
        if (!cached || !cached.payload || age < 0 || (staleTtl > 0 && age > staleTtl)) continue;

        var sessionStorage = getStorage('sessionStorage');
        if (storage !== sessionStorage) {
          writeStorageItem(sessionStorage, options.key, raw);
        }

        return {
          isFresh: freshTtl > 0 ? age <= freshTtl : true,
          payload: cached.payload
        };
      } catch (error) {
        // Ignore malformed cache entries.
      }
    }

    return null;
  }

  function writeJsonCache(options) {
    var value = JSON.stringify({
      createdAt: Date.now(),
      payload: options.payload
    });
    var stores = options.stores || [getStorage('sessionStorage'), getStorage('localStorage')];
    stores.forEach(function (storage) {
      writeStorageItem(storage, options.key, value);
    });
  }

  function preconnect(url, namespace) {
    try {
      var origin = new URL(url).origin;
      var attr = 'data-' + (namespace || 'item-manager') + '-preconnect';
      if (document.querySelector('link[' + attr + '="' + origin + '"]')) return;

      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      link.setAttribute(attr, origin);
      document.head.appendChild(link);
    } catch (error) {
      // Invalid URLs are handled at fetch time; preconnect is best effort.
    }
  }

  function refreshCarousel(section) {
    window.requestAnimationFrame(function () {
      var carousel = section && section.querySelector('carousel-slider');
      if (carousel && typeof carousel.refresh === 'function') carousel.refresh();
    });
  }

  function setupNavCenter(options) {
    var section = options.section;
    var frame = options.frame;
    var list = options.list;
    var targetSelector = options.targetSelector;
    var observedTarget = null;
    var observer = null;
    var syncScheduled = false;

    function sync() {
      if (!section || !frame) return;
      var target = section.querySelector(targetSelector);
      if (!target) return;

      if (observer && target !== observedTarget) {
        if (observedTarget) observer.unobserve(observedTarget);
        observer.observe(target);
        observedTarget = target;
      }

      var frameRect = frame.getBoundingClientRect();
      var targetRect = target.getBoundingClientRect();
      if (!targetRect.height) return;

      var center = targetRect.top - frameRect.top + (targetRect.height / 2);
      var nextValue = String(Math.round(center * 100) / 100) + 'px';
      if (section.style.getPropertyValue('--item-manager-nav-center') !== nextValue) {
        section.style.setProperty('--item-manager-nav-center', nextValue);
      }
    }

    function schedule() {
      if (syncScheduled) return;
      syncScheduled = true;
      window.requestAnimationFrame(function () {
        sync();
        window.requestAnimationFrame(function () {
          sync();
          syncScheduled = false;
        });
      });
    }

    function disconnect() {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', schedule);
    }

    if ('ResizeObserver' in window && frame) {
      observer = new ResizeObserver(schedule);
      observer.observe(frame);
      if (list) observer.observe(list);
    }

    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target && section && event.target.contains(section)) disconnect();
    });

    schedule();

    return {
      disconnect: disconnect,
      schedule: schedule,
      sync: sync
    };
  }

  var shopifyProductCache = new Map();
  var missingProductHandles = {};
  var productFetchQueue = [];
  var activeProductFetches = 0;
  var MAX_CONCURRENT_PRODUCT_FETCHES = 4;

  function pumpProductFetchQueue() {
    while (activeProductFetches < MAX_CONCURRENT_PRODUCT_FETCHES && productFetchQueue.length) {
      var job = productFetchQueue.shift();
      activeProductFetches += 1;
      job
        .task()
        .then(job.resolve, function () { job.resolve(null); })
        .then(function () {
          activeProductFetches -= 1;
          pumpProductFetchQueue();
        });
    }
  }

  function enqueueProductFetch(task) {
    return new Promise(function (resolve) {
      productFetchQueue.push({ task: task, resolve: resolve });
      pumpProductFetchQueue();
    });
  }

  function normalizeSwatchValue(value) {
    return String(value == null ? '' : value).replace(/"/g, '').trim().toLowerCase();
  }

  function productJsonUrl(handle) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '.js';
  }

  /**
   * Shared product JSON fetcher. Deduplicates by handle, caps concurrency so a
   * page that needs many products does not fire dozens of requests at once, and
   * records handles that return a hard 404 so callers can avoid re-requesting
   * deleted/unpublished products. Resolves to null on any failure.
   */
  function fetchShopifyProduct(handle) {
    var key = String(handle || '').trim();
    if (!key) return Promise.resolve(null);
    if (!shopifyProductCache.has(key)) {
      shopifyProductCache.set(
        key,
        enqueueProductFetch(function () {
          return fetch(productJsonUrl(key), {
            headers: { Accept: 'application/json' },
            priority: 'low'
          })
            .then(function (response) {
              if (response.status === 404) {
                missingProductHandles[key] = true;
                return null;
              }
              if (!response.ok) return null;
              return response.json();
            })
            .catch(function () {
              return null;
            });
        })
      );
    }

    return shopifyProductCache.get(key);
  }

  function isProductMissing(handle) {
    return missingProductHandles[String(handle || '').trim()] === true;
  }

  function optionName(option) {
    return String(option && option.name || option || '').trim().toLowerCase();
  }

  function getColorOptionIndex(product) {
    var options = Array.isArray(product && product.options) ? product.options : [];
    for (var index = 0; index < options.length; index += 1) {
      var name = optionName(options[index]);
      if (name === 'color' || name === 'colour' || name === 'カラー' || name === '色') {
        return index;
      }
    }

    var firstVariant = Array.isArray(product && product.variants) ? product.variants[0] : null;
    if (firstVariant && (firstVariant.option2 || Array.isArray(firstVariant.options) && firstVariant.options.length > 1)) {
      return 1;
    }

    return 0;
  }

  function getVariantOptionValue(variant, optionIndex) {
    if (!variant) return '';
    if (Array.isArray(variant.options) && variant.options[optionIndex]) {
      return variant.options[optionIndex];
    }
    return variant['option' + (optionIndex + 1)] || '';
  }

  function getVariantImageUrl(variant) {
    var src = variant && variant.featured_image && variant.featured_image.src ||
      variant && variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src ||
      '';
    return resizeShopifyImageUrl(src, 900);
  }

  function resizeShopifyImageUrl(src, width) {
    if (!src || !width) return src || '';
    try {
      var url = new URL(src, window.location.origin);
      if (url.hostname.indexOf('cdn.shopify.com') === -1) return src;
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  function getVariantColorImageMap(product) {
    var map = {};
    var optionIndex = getColorOptionIndex(product);
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    variants.forEach(function (variant) {
      var value = normalizeSwatchValue(getVariantOptionValue(variant, optionIndex));
      var imageUrl = getVariantImageUrl(variant);
      if (value && imageUrl && !map[value]) {
        map[value] = imageUrl;
      }
    });
    return map;
  }

  function getColorOptionValues(product) {
    var optionIndex = getColorOptionIndex(product);
    var options = Array.isArray(product && product.options) ? product.options : [];
    var option = options[optionIndex];
    var values = Array.isArray(option && option.values) ? option.values : [];

    if (!values.length) {
      var seen = {};
      var variants = Array.isArray(product && product.variants) ? product.variants : [];
      variants.forEach(function (variant) {
        var value = String(getVariantOptionValue(variant, optionIndex) || '').trim();
        var key = normalizeSwatchValue(value);
        if (value && !seen[key]) {
          seen[key] = true;
          values.push(value);
        }
      });
    }

    return values.map(function (value) {
      return String(value && value.name || value || '').trim();
    }).filter(Boolean);
  }

  function isColorValueUnavailable(product, value) {
    var optionIndex = getColorOptionIndex(product);
    var target = normalizeSwatchValue(value);
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    var matched = false;
    var available = false;

    variants.forEach(function (variant) {
      if (normalizeSwatchValue(getVariantOptionValue(variant, optionIndex)) !== target) return;
      matched = true;
      if (variant.available) available = true;
    });

    return matched && !available;
  }

  function findDirectSwatch(inner, value) {
    var target = normalizeSwatchValue(value);
    var items = Array.prototype.slice.call(inner.children || []);
    for (var index = 0; index < items.length; index += 1) {
      var item = items[index];
      if (!item.classList || !item.classList.contains('product-block-options__item')) continue;
      if (normalizeSwatchValue(item.dataset.optionItem || item.textContent) === target) return item;
    }
    return null;
  }

  function ensureSwatchItem(inner, value) {
    var swatch = findDirectSwatch(inner, value);
    if (swatch) return swatch;

    swatch = document.createElement('span');
    swatch.className = 'product-block-options__item';
    swatch.dataset.optionItem = value;
    swatch.dataset.swatch = normalizeSwatchValue(value);

    var text = document.createElement('span');
    text.className = 'product-block-options__item__text';
    text.textContent = value;
    swatch.appendChild(text);
    inner.appendChild(swatch);
    return swatch;
  }

  function hydrateColorImageSwatches(card, options, product) {
    if (!card || !product) return;
    var imageByColor = getVariantColorImageMap(product);
    var authority = options.colorSwatchAuthority
      || (options.ensureAllSwatches === false ? 'item-manager' : 'union');
    var inner = card.querySelector(options.swatchInnerSelector || '.product-block-options__inner');
    var shopifyColors = getColorOptionValues(product);

    if (inner && authority === 'shopify' && shopifyColors.length > 0) {
      var shopifyKeys = {};
      shopifyColors.forEach(function (value) {
        shopifyKeys[normalizeSwatchValue(value)] = true;
      });
      Array.prototype.slice.call(inner.children).forEach(function (el) {
        if (!el.classList || !el.classList.contains('product-block-options__item')) return;
        var key = normalizeSwatchValue(el.dataset.optionItem || el.textContent);
        if (!shopifyKeys[key]) inner.removeChild(el);
      });
      shopifyColors.forEach(function (value) {
        ensureSwatchItem(inner, value);
      });
    } else if (inner && authority === 'union') {
      shopifyColors.forEach(function (value) {
        ensureSwatchItem(inner, value);
      });
    }

    card.querySelectorAll(options.swatchSelector).forEach(function (swatch) {
      var value = normalizeSwatchValue(swatch.dataset.optionItem || swatch.textContent);
      swatch.classList.toggle('product-block-options__item--unavailable', isColorValueUnavailable(product, value));
      if (value && imageByColor[value]) {
        swatch.dataset.colorImageUrl = imageByColor[value];
      }
    });
  }

  function preloadColorImages(card, options) {
    var seen = {};
    card.querySelectorAll(options.swatchSelector).forEach(function (swatch) {
      var imageUrl = swatch.dataset.colorImageUrl;
      if (!imageUrl || seen[imageUrl]) return;
      seen[imageUrl] = true;

      var image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      image.src = imageUrl;
    });
  }

  function hydrateColorImageSwatchCard(card, options) {
    var handle = card && card.getAttribute(options.handleAttribute || 'data-product-handle');
    if (!handle) return Promise.resolve(null);

    return fetchShopifyProduct(handle).then(function (product) {
      hydrateColorImageSwatches(card, options, product);
      if (options.preloadImages !== false) preloadColorImages(card, options);
      return product;
    });
  }

  function hydrateColorImageSwatchCards(options) {
    var root = options.root;
    if (!root) return Promise.resolve([]);

    var cards = Array.prototype.slice.call(root.querySelectorAll(options.productSelector));
    return Promise.all(cards.map(function (card) {
      return hydrateColorImageSwatchCard(card, options);
    }));
  }

  function bindColorImageSwatches(options) {
    var root = options.root;
    if (!root) return;

    var pending = new WeakMap();
    var activeSwatch = new WeakMap();

    function setImage(card, swatch) {
      var imageUrl = swatch && swatch.dataset.colorImageUrl;
      if (!card || !imageUrl) return;

      var image = card.querySelector(options.imageSelector);
      if (!image) return;

      image.removeAttribute('srcset');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
      image.src = imageUrl;
      card.querySelectorAll(options.swatchSelector).forEach(function (item) {
        item.classList.toggle('is-active', item === swatch);
      });
    }

    function hydrateAndSet(card, swatch) {
      var handle = card.getAttribute(options.handleAttribute || 'data-product-handle');
      if (!handle) return;

      var request = pending.get(card);
      if (!request) {
        request = hydrateColorImageSwatchCard(card, options);
        pending.set(card, request);
      }

      request.then(function () {
        if (activeSwatch.get(card) === swatch) {
          setImage(card, swatch);
        }
      });
    }

    function handleSwatch(event) {
      if (!(event.target instanceof Element)) return;
      var swatch = event.target.closest(options.swatchSelector);
      if (!swatch || !root.contains(swatch)) return;

      if (event.type === 'click') event.preventDefault();

      var card = swatch.closest(options.productSelector);
      if (!card) return;

      activeSwatch.set(card, swatch);

      if (swatch.dataset.colorImageUrl) {
        setImage(card, swatch);
        return;
      }

      hydrateAndSet(card, swatch);
    }

    root.addEventListener('mouseover', handleSwatch);
    root.addEventListener('focusin', handleSwatch);
    root.addEventListener('click', handleSwatch);
  }

  // --- 価格ヘルパー（Shopify product JSON ベース）----------------------------
  // 商品カテゴリー（snippets/price.liquid）と完全に同じ基準でセール価格を出すための
  // 単一の真実。価格の取得・判定・整形・HTML生成をここに集約し、各セクションは
  // これを呼ぶだけにすることで、単位の取り違えや判定ズレを起こさない。
  //
  //  - 基準は「最安バリアントの price と compare_at_price」（price.liquid の
  //    `variant = product.variants | sort: 'price' | first` と一致）。
  //  - Shopify の price は最小通貨単位（JPY は円×100）なので必ず 100 で割る。
  //  - セールは `compare_at_price > price` のときだけ（price.liquid と同じ厳密判定）。

  function pickCheapestVariant(product) {
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    var chosen = null;
    for (var index = 0; index < variants.length; index += 1) {
      var variant = variants[index];
      var price = Number(variant && variant.price);
      if (!Number.isFinite(price)) continue;
      // price.liquid の `sort: 'price' | first`（安定ソート）と同じく、
      // 最安かつ配列順で最初のバリアントを採用する（< のみで更新）。
      if (!chosen || price < chosen.price) {
        chosen = { price: price, compareAt: Number(variant.compare_at_price) || 0 };
      }
    }
    return chosen;
  }

  // Shopify の product JSON から表示価格データを取り出す。
  // 戻り値: { priceCents, compareAtCents, onSale } または取得不能時は null。
  function getShopifyDisplayPrice(product) {
    if (!product) return null;
    var chosen = pickCheapestVariant(product);
    if (!chosen) {
      // variants を持たないレスポンス向けのフォールバック（price_min / price）。
      var topPrice = Number(product.price_min);
      if (!Number.isFinite(topPrice)) topPrice = Number(product.price);
      if (!Number.isFinite(topPrice)) return null;
      var topCompare = Number(product.compare_at_price_min);
      if (!Number.isFinite(topCompare)) topCompare = Number(product.compare_at_price);
      chosen = { price: topPrice, compareAt: Number.isFinite(topCompare) ? topCompare : 0 };
    }
    return {
      priceCents: chosen.price,
      compareAtCents: chosen.compareAt,
      onSale: chosen.compareAt > chosen.price
    };
  }

  // Shopify の最小通貨単位（整数。JPY は円×100）を「¥6,600」表記へ整形する。
  // price.liquid の `{{ price | money }}` に表記を揃える。
  function formatShopifyMoney(cents) {
    var amount = Number(cents);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    return '¥' + Math.round(amount / 100).toLocaleString('ja-JP');
  }

  // 商品カード用の価格 HTML（.price）。商品カテゴリーと同型で、セール時のみ
  // price--on-sale と price__was（元値・取り消し線）を出す。バッジ等は付けない。
  // 戻り値が空文字のときは呼び出し側で従来のフォールバック表示に切り替える。
  function renderBlockPriceHtml(priceData) {
    if (!priceData) return '';
    var current = formatShopifyMoney(priceData.priceCents);
    if (!current) return '';
    var currentHtml = '<span class="price__current"><span>' + escapeHtml(current) + '</span></span>';
    if (!priceData.onSale) {
      return '<div class="price"><div class="price__default">' + currentHtml + '</div></div>';
    }
    var was = formatShopifyMoney(priceData.compareAtCents);
    var wasHtml = was ? '<span class="price__was"><span>' + escapeHtml(was) + '</span></span>' : '';
    return '<div class="price price--on-sale"><div class="price__default">' + currentHtml + wasHtml + '</div></div>';
  }

  window.ItemManagerThemeUtils = {
    escapeHtml: escapeHtml,
    bindColorImageSwatches: bindColorImageSwatches,
    fetchShopifyProduct: fetchShopifyProduct,
    getShopifyDisplayPrice: getShopifyDisplayPrice,
    formatShopifyMoney: formatShopifyMoney,
    renderBlockPriceHtml: renderBlockPriceHtml,
    isProductMissing: isProductMissing,
    getStorage: getStorage,
    hydrateColorImageSwatchCards: hydrateColorImageSwatchCards,
    hydrateColorImageSwatches: hydrateColorImageSwatches,
    normalizeSwatchValue: normalizeSwatchValue,
    preconnect: preconnect,
    readJsonCache: readJsonCache,
    readStorageItem: readStorageItem,
    refreshCarousel: refreshCarousel,
    removeStorageItem: removeStorageItem,
    setupNavCenter: setupNavCenter,
    writeJsonCache: writeJsonCache,
    writeStorageItem: writeStorageItem
  };
  notifyReady();
})();
